/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { Button } from 'antd';
import { formatPercentage } from './i18n';
import { STATUS } from '../constants/status';

const NOTIFICATIONS = {
  ANNOUNCEMENT_CREATED: 'Announcement Created',
  ANNOUNCEMENT_UPDATED: 'Announcement Updated',
  MARKET_PARTY_REGISTERED: 'Market Party Registered',
  BALANCE_GROUP_REQUEST: 'Balance Group Request',
  BALANCE_GROUP_RESPONSE: 'Balance Group Response',
  BALANCE_GROUP_CHANGE: 'Balance Group Change',
  BALANCE_GROUP_MEMBER_REQUEST: 'Balance Group Member Request',
  BALANCE_GROUP_MEMBER_RESPONSE: 'Balance Group Member Response',
  BALANCE_GROUP_MEMBER_CHANGE: 'Balance Group Member Change',
  CAPACITY_RESERVATION_REQUEST: 'Capacity Reservation Request',
  CAPACITY_RESERVATION_RESPONSE: 'Capacity Reservation Response',
  CAPACITY_TRANSFER_REQUEST: 'Capacity Transfer Request',
  CAPACITY_TRANSFER_RESPONSE: 'Capacity Transfer Response',
  DELIVERY_RELATIONSHIP_REQUEST: 'Delivery Relationship Request',
  DELIVERY_RELATIONSHIP_RESPONSE: 'Delivery Relationship Response',
  MARKET_RELATIONSHIP_REQUEST: 'Market Party Relationship Request',
  MARKET_RELATIONSHIP_RESPONSE: 'Market Party Relationship Response',
  IMBALANCE: 'Imbalance',
  BALANCE_GAS_PRICE_UPDATE: 'Balance gas price update',
};

const renderMessage = (messageText, actionText, url, onActionClick, notificationKey) => {
  const messageTexts = Array.isArray(messageText) ? messageText : [messageText];
  return (
    <div>
      {messageTexts.filter((message) => (message !== undefined)).map((message, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={i}>
          {message}
        </p>
      ))}
      <Button
        style={{ float: 'right' }}
        onClick={() => onActionClick(url, notificationKey)}
        type="primary"
      >
        {actionText}
      </Button>
    </div>
  );
};

const parseNotification = (data, onActionClick, { t }) => {
  switch (data.type) {
    case NOTIFICATIONS.MARKET_PARTY_REGISTERED: {
      const {
        marketParty,
      } = data;

      const message = renderMessage(
        'Newly registered market party needs your approval.',
        'Show new market party',
        `/marketparties/${marketParty.id}`,
        onActionClick,
      );

      return {
        subject: 'New Market Party registered',
        message,
      };
    }

    case NOTIFICATIONS.ANNOUNCEMENT_CREATED: {
      const { id, subject } = data;
      const message = renderMessage(
        subject,
        'Show announcement',
        `/frontpage#announcement-${id}`,
        onActionClick,
      );

      return {
        subject: 'Announcement',
        message,
      };
    }

    case NOTIFICATIONS.ANNOUNCEMENT_UPDATED: {
      const { id, subject } = data;
      const message = renderMessage(
        subject,
        'Show announcement',
        `/frontpage#announcement-${id}`,
        onActionClick,
      );

      return {
        subject: 'Announcement updated',
        message,
      };
    }

    case NOTIFICATIONS.BALANCE_GROUP_REQUEST: {
      const {
        balanceGroupId,
      } = data;

      const message = renderMessage(
        'Balance Group Request needs your approval.',
        'Show balance group',
        `/balancegroups/${balanceGroupId}`,
        onActionClick,
      );

      return {
        subject: 'Balance Group Request',
        message,
      };
    }

    case NOTIFICATIONS.BALANCE_GROUP_RESPONSE: {
      const {
        balanceGroupId,
        status,
      } = data;

      const message = renderMessage(
        `Balance Group Response: ${status}`,
        'Show balance group',
        `/balancegroups/${balanceGroupId}`,
        onActionClick,
      );

      return {
        subject: 'Balance Group Response',
        message,
      };
    }

    case NOTIFICATIONS.BALANCE_GROUP_CHANGE: {
      const {
        balanceGroupId,
        oldEnd,
        end,
      } = data;

      const message = renderMessage(
        `Balance Group Change (end: ${oldEnd} -> ${end})`,
        'Show balance group',
        `/balancegroups/${balanceGroupId}`,
        onActionClick,
      );

      return {
        subject: 'Balance Group Change',
        message,
      };
    }

    case NOTIFICATIONS.BALANCE_GROUP_MEMBER_REQUEST: {
      const {
        balanceGroupMembershipId,
      } = data;

      const message = renderMessage(
        'Balance Group Member Request needs your approval',
        'Show membership request',
        `/balancegroups/membership/${balanceGroupMembershipId}`,
        onActionClick,
      );

      return {
        subject: 'Balance Group Member Request',
        message,
      };
    }

    case NOTIFICATIONS.BALANCE_GROUP_MEMBER_RESPONSE: {
      const {
        balanceGroupMembershipId,
        status,
      } = data;

      const message = renderMessage(
        `Balance Group Member Response: ${status}`,
        'Show membership',
        `/balancegroups/membership/${balanceGroupMembershipId}`,
        onActionClick,
      );

      return {
        subject: 'Balance Group Member Response',
        message,
      };
    }

    case NOTIFICATIONS.BALANCE_GROUP_MEMBER_CHANGE: {
      const {
        balanceGroupMembershipId,
        oldEnd,
        end,
      } = data;

      const message = renderMessage(
        `Balance Group Member Change (end: ${oldEnd} -> ${end})`,
        'Show membership',
        `/balancegroups/membership/${balanceGroupMembershipId}`,
        onActionClick,
      );

      return {
        subject: 'Balance Group Member Change',
        message,
      };
    }

    // TODO: Use correct url for specific capacity reservation
    case NOTIFICATIONS.CAPACITY_RESERVATION_REQUEST: {
      const {
        reservationId,
        marketPartyId,
        message: notes,
      } = data;

      const key = `${data.type}#${reservationId}`;

      const message = renderMessage(
        [
          'Capacity Reservation Request needs your approval.',
          notes,
        ],
        'Show reservation',
        `/capacitybookings/${reservationId}/${marketPartyId}`,
        onActionClick,
        key,
      );

      return {
        subject: 'Capacity Reservation Request',
        message,
        options: {
          duration: 60,
          key,
        },
      };
    }

    // TODO: Use correct url for specific capacity reservation
    case NOTIFICATIONS.CAPACITY_RESERVATION_RESPONSE: {
      const {
        reservationId,
        marketPartyId,
        status,
        points,
        message: notes,
      } = data;

      const key = `${data.type}#${reservationId}`;

      const point = t(`capacity.point.${points?.[0]}`);
      // const range = getTimeRangeOfProducts(products);
      // const reservedFor = `${formatDateTimeStr(range.start)} - ${formatDateTimeStr(range.end)}`;

      const message = renderMessage(
        [
          (<>Your reservation for <b>{point}</b> is now:</>),
          (<b>{t(`common.status.${status}`)}</b>),
          notes,
        ],
        'Show booking',
        `/capacitybookings/${reservationId}/${marketPartyId}`,
        onActionClick,
        key,
      );

      return {
        subject: 'Capacity Reservation Response',
        message,
        options: {
          duration: status === STATUS.DECLINED ? 0 : 30,
          key,
        },
      };
    }

    // TODO: Use correct url for specific capacity transfer
    case NOTIFICATIONS.CAPACITY_TRANSFER_REQUEST: {
      const message = renderMessage(
        'Capacity Transfer Request needs your approval.',
        'Show transfers',
        '/capacityrightstransfers',
        onActionClick,
      );

      return {
        subject: 'Capacity Transfer Request',
        message,
      };
    }

    // TODO: Use correct url for specific capacity transfer
    case NOTIFICATIONS.CAPACITY_TRANSFER_RESPONSE: {
      const {
        buyerStatus,
        status,
      } = data;

      const message = renderMessage(
        // TODO: More descriptive message?
        `Capacity Transfer Response: ${(status !== undefined) ? status : `${buyerStatus} by buyer`}`,
        'Show transfers',
        '/capacityrightstransfers',
        onActionClick,
      );

      return {
        subject: 'Capacity Transfer Response',
        message,
      };
    }

    case NOTIFICATIONS.DELIVERY_RELATIONSHIP_REQUEST: {
      const message = renderMessage(
        'Delivery Relationship Request needs your approval.',
        'Show relationships',
        '/deliveryrelationships',
        onActionClick,
      );

      return {
        subject: 'Delivery Relationship Request',
        message,
      };
    }

    case NOTIFICATIONS.DELIVERY_RELATIONSHIP_RESPONSE: {
      const {
        relationship: {
          status,
        },
      } = data;

      const message = renderMessage(
        `Delivery Relationship Response: ${status}`,
        'Show relationships',
        '/deliveryrelationships',
        onActionClick,
      );

      return {
        subject: 'Delivery Relationship Response',
        message,
      };
    }

    case NOTIFICATIONS.MARKET_RELATIONSHIP_REQUEST: {
      const message = renderMessage(
        'Market Party Relationship Request needs your approval.',
        'Show relationships',
        '/relationships-received',
        onActionClick,
      );

      return {
        subject: 'Market Party Relationship Request',
        message,
      };
    }

    case NOTIFICATIONS.MARKET_RELATIONSHIP_RESPONSE: {
      const {
        relationship: {
          status,
        },
      } = data;

      const message = renderMessage(
        `Market Party Relationship Response: ${status}`,
        'Show relationships',
        '/relationships-created',
        onActionClick,
      );

      return {
        subject: 'Market Party Relationship Response',
        message,
      };
    }

    case NOTIFICATIONS.IMBALANCE: {
      const {
        percentage,
        previousPercentage,
        updatedStatus,
        previousStatus,
      } = data;

      const message = renderMessage(
        <>
          {`Current: ${formatPercentage(percentage)} (${updatedStatus})`}
          <br />
          {`Previous: ${formatPercentage(previousPercentage)} (${previousStatus})`}
        </>,
        'Show monitoring',
        '/capacityandnominations',
        onActionClick,
      );

      return {
        subject: 'Imbalance percentage change',
        message,
        options: {
          duration: 0,
        },
      };
    }

    case NOTIFICATIONS.BALANCE_GAS_PRICE_UPDATE: {
      const { buy, sell } = data;
      const buyPrice = buy ? `Buy price: ${buy} €/MWh` : '';
      const sellPrice = sell ? `Sell price: ${sell} €/MWh` : '';

      const message = renderMessage(
        <>
          {buyPrice}
          <br />
          {sellPrice}
        </>,
        'Show front page',
        '/frontpage',
        onActionClick,
      );

      return {
        subject: 'New balance gas price',
        message,
      };
    }

    default:
      throw new Error(`Unknown notification type: ${data.type}`);
  }
};

export {
  NOTIFICATIONS,
  parseNotification,
};
